import {
  IOnBoarding,
  IOnboardingDraft,
  IRouterPaginationResponse,
} from "interfaces";
import { institutionalApi } from "./index";
import { buildUrlWithFilters, transformErrorResponse } from "../../utils";
import { CreateUserRequest, KYBQuery } from "interfaces/router";

export const onBoardingApi = institutionalApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllBrokers: builder.query<any, void>({
      query: () => ({
        url: `/kyb/broker_name_map`,
      }),
    }),
    getAllOnboardings: builder.query<
      IRouterPaginationResponse<IOnBoarding>,
      KYBQuery
    >({
      query: ({ endpoint, ...props }) => ({
        url: buildUrlWithFilters(`/kyb/${endpoint}`, props),
      }),
      providesTags: ["Onboardings"],
      transformErrorResponse,
    }),
    getAllOnboardingStatus: builder.query<{ [key: string]: number }, void>({
      query: () => ({
        url: `/kyb/status_counts`,
      }),
      providesTags: ["OnboardingsStatus"],
      transformErrorResponse,
    }),
    getOnboardingDocument: builder.query<
      string,
      { id: string; filename: string }
    >({
      query: (params) => ({
        url: `/kyb/document/${params.id}`,
        params: { filename: params.filename },
      }),
      transformErrorResponse,
    }),
    getOnboarding: builder.query<IOnBoarding, { id: string }>({
      query: (params) => ({
        url: `/kyb`,
        params: { kyb_id: params.id },
      }),
      providesTags: (_, __, args) => [{ type: "Onboarding", id: args.id }],
      transformErrorResponse,
    }),
    updateOnboardingUnRegulated: builder.mutation<
      IOnBoarding,
      IOnboardingDraft
    >({
      query: (params) => ({
        url: `/kyb/unregulated/${params.id}`,
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["Onboarding"],
      transformErrorResponse,
    }),
    updateOnboardingRegulated: builder.mutation<IOnBoarding, IOnboardingDraft>({
      query: (params) => ({
        url: `/kyb/regulated/${params.id}`,
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["Onboarding"],
      transformErrorResponse,
    }),
    createOnboarding: builder.mutation<IOnBoarding, IOnboardingDraft>({
      query: (params) => ({
        url: `/kyb/kyb_email`,
        method: "POST",
        params,
      }),
      invalidatesTags: ["Onboardings"],
      transformErrorResponse,
    }),
    changeOnboardingStatus: builder.mutation<IOnBoarding, IOnboardingDraft>({
      query: (body) => ({
        url: `/kyb/status/${body.id}`,
        method: "POST",
        params: { status: body.status },
      }),
      invalidatesTags: ["Onboardings", "OnboardingsStatus"],
      transformErrorResponse,
    }),
    addDataKYB: builder.mutation<
      IOnboardingDraft,
      { extracted_data_map: unknown; id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/kyb/extracted_data/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Onboardings"],
      transformErrorResponse,
    }),
    changeOnboardingAnalyst: builder.mutation<
      any,
      { kyb_id: string | undefined; user_id: string }
    >({
      query: ({ kyb_id, user_id }) => ({
        url: `/kyb/analyst/${kyb_id}`,
        method: "POST",
        params: { user_id: user_id },
      }),
      invalidatesTags: ["Onboardings"],
    }),
    changeOnboardingDecider: builder.mutation<
      any,
      { kyb_id: string | undefined; user_id: string }
    >({
      query: ({ kyb_id, user_id }) => ({
        url: `/kyb/decider/${kyb_id}`,
        method: "POST",
        params: { user_id: user_id },
      }),
      invalidatesTags: ["Onboardings"],
    }),
    changeOnboardingPrioritization: builder.mutation<
      any,
      { kyb_id: string | undefined; prioritization_level: number }
    >({
      query: ({ kyb_id, prioritization_level }) => ({
        url: `/kyb/prioritization/${kyb_id}`,
        method: "POST",
        params: { prioritization: prioritization_level },
      }),
      invalidatesTags: ["Onboardings"],
    }),
    addDocumentsKYB: builder.mutation<"success", { files: any; id: string }>({
      query: ({ id, ...body }) => ({
        url: `/kyb/additional_docs/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, params) => [
        "Onboardings",
        { type: "Onboarding", id: params.id },
      ],
      transformErrorResponse,
    }),
    getClients: builder.query<{ [k: string]: string }, void>({
      query: () => ({
        url: "/router/admin/client/alias",
        method: "GET",
      }),
    }),
    getAdminEmails: builder.query<Array<{ value: string; label: string }>, any>(
      {
        query: () => ({
          url: "router/admin/user/admin_user_emails",
        }),
        transformResponse: (response: Record<string, string>) => {
          return Object.entries(response).map(([user_id, email]) => ({
            value: user_id,
            label: email,
          }));
        },
        providesTags: ["User"],
      },
    ),
    createUserFromKYB: builder.mutation<"success", CreateUserRequest>({
      query: (body) => ({
        url: `/kyb/create_user/`,
        method: "POST",
        body,
      }),
      // invalidatesTags: (_, __, params) => ["Onboardings"],
      transformErrorResponse,
    }),
  }),
});

export const {
  useGetAllOnboardingStatusQuery,
  useCreateUserFromKYBMutation,
  useGetClientsQuery,
  useAddDocumentsKYBMutation,
  useAddDataKYBMutation,
  useGetOnboardingQuery,
  useGetAllOnboardingsQuery,
  useGetAllBrokersQuery,
  useChangeOnboardingStatusMutation,
  useCreateOnboardingMutation,
  useLazyGetOnboardingDocumentQuery,
  useUpdateOnboardingRegulatedMutation,
  useUpdateOnboardingUnRegulatedMutation,
  useGetAdminEmailsQuery,
  useChangeOnboardingAnalystMutation,
  useChangeOnboardingDeciderMutation,
  useChangeOnboardingPrioritizationMutation,
} = onBoardingApi;
